import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, CheckIcon, HeartHandshakeIcon, LightbulbIcon, LockKeyholeIcon, MessageCircleHeartIcon, ShieldCheckIcon, SparklesIcon, UserRoundCheckIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { FaqAccordion } from '../components/FaqAccordion';
type IconCard = {
  icon: typeof HeartHandshakeIcon;
  title: string;
  description: string;
};
type Inclusion = {
  label: string;
  title: string;
  description: string;
  bonusValue?: string;
};
type AccessItem = {
  title: string;
  bonusValue?: string;
};
const images = {
  book: 'https://cdn.magicpatterns.com/uploads/ohWVCJZx1ms6byVa6yHng2/El-Codigo-de-la-Reconquista-07-13-2026_06_26_PM.png',
  hero: 'https://cdn.magicpatterns.com/uploads/5uEdQVXYczZvXcmXgbi5s5/rcq-hero.jpg',
  author: 'https://cdn.magicpatterns.com/uploads/jBKdbBVUo8LSc5KX2VanM8/rcq-autora.jpg',
  transformation: 'https://cdn.magicpatterns.com/uploads/93K5qhMVVh2e1Rhq6SdRs6/rcq-transformacion.jpg',
  cta: 'https://cdn.magicpatterns.com/uploads/47356Md8YoZKqVoX7tZAPZ/rcq-cta.jpg',
  problem: 'https://cdn.magicpatterns.com/uploads/ghnU9dcWUd3RvrsddSLBmL/rcq-problema.jpg'
};
const failurePoints = ['Enviaste mensajes largos explicando tus sentimientos o pidiendo otra oportunidad, y la distancia aumentó.', 'Intentaste el contacto cero, pero regresaste antes de sentirte lista y perdiste claridad sobre el proceso.', 'Buscaste consejos en internet y encontraste ideas contradictorias que te dejaron aún más confundida.', 'Publicaste en redes para aparentar que estabas bien, pero el mensaje no fue auténtico ni te dio tranquilidad.', 'Pediste consejos a personas cercanas, aunque su experiencia no se parecía a la tuya.', 'Actuaste desde la desesperación y esa urgencia hizo más difícil sostener una conversación serena.'];
const approachCards: IconCard[] = [{
  icon: MessageCircleHeartIcon,
  title: 'Mensajes',
  description: 'Estructuras claras para saber qué decir, cuándo decirlo y cómo expresarte sin dejar de ser tú.'
}, {
  icon: LightbulbIcon,
  title: 'Psicología',
  description: 'Una mirada comprensible a las dinámicas emocionales que aparecen después de una ruptura.'
}, {
  icon: SparklesIcon,
  title: 'Plan',
  description: 'Pasos en orden para la primera semana, el primer mes y los momentos en que necesitas pausar.'
}];
const inclusions: Inclusion[] = [{
  label: 'Incluido',
  title: 'El Código de la Reconquista — Guía principal',
  description: 'El método completo: desde el primer contacto hasta una posible reconciliación, con herramientas de comunicación y un mapa emocional del proceso.'
}, {
  label: 'Bono #1',
  title: 'Kit antiansiedad de reconexión',
  description: 'Recursos prácticos para recuperar calma y evitar actuar desde la urgencia cuando el vínculo se siente incierto.',
  bonusValue: 'USD$13'
}, {
  label: 'Bono #2',
  title: 'Guía de autoestima en medio del distanciamiento',
  description: 'Ejercicios y perspectivas para cuidar tu seguridad personal mientras atraviesas este momento.',
  bonusValue: 'USD$12'
}, {
  label: 'Bono #3',
  title: 'Mapa de señales reales de progreso',
  description: 'Una guía para observar avances con perspectiva, sin depender de interpretaciones apresuradas.',
  bonusValue: 'USD$12'
}, {
  label: 'Bono #4',
  title: 'Guiones de reconexión para momentos difíciles',
  description: 'Referencias de comunicación para acercarte con claridad en conversaciones sensibles.',
  bonusValue: 'USD$12'
}, {
  label: 'Bono #5',
  title: 'Presencia que reconecta',
  description: 'Prácticas de presencia, lenguaje corporal y seguridad personal para un reencuentro más auténtico.',
  bonusValue: 'USD$13'
}];
const qualificationItems = ['La ruptura es reciente y sientes que todavía existe una conexión real entre ustedes.', 'Llevas semanas en silencio y no sabes cuál es el siguiente paso más respetuoso.', 'Probaste distintas opciones y ahora quieres un método, no consejos genéricos.', 'La otra persona está distante o te ha bloqueado y necesitas recuperar perspectiva antes de actuar.', 'Quieres comprender mejor las dinámicas emocionales después de una ruptura.', 'Sabes que la relación fue significativa y quieres acercarte sin renunciar a tu dignidad.'];
const accessItems: AccessItem[] = [{
  title: 'Guía principal El Código de la Reconquista — un recorrido paso a paso.'
}, {
  title: 'Kit antiansiedad de reconexión',
  bonusValue: 'USD$13'
}, {
  title: 'Guía de autoestima en medio del distanciamiento',
  bonusValue: 'USD$12'
}, {
  title: 'Mapa de señales reales de progreso',
  bonusValue: 'USD$12'
}, {
  title: 'Guiones de reconexión para momentos difíciles',
  bonusValue: 'USD$12'
}, {
  title: 'Presencia que reconecta',
  bonusValue: 'USD$13'
}, {
  title: 'Acceso inmediato y permanente desde móvil, tablet u ordenador.'
}, {
  title: 'Garantía de 7 días para revisar el material con calma.'
}];
const faqs = [{
  question: '¿Cuánto tiempo tarda en dar resultados?',
  answer: 'Cada historia tiene su propio ritmo y no hay un plazo garantizado. Algunas personas notan más claridad en su forma de comunicarse desde los primeros días; una reconexión depende de ambas personas, de su historia y de que exista disposición mutua.'
}, {
  question: '¿Funciona si llevamos mucho tiempo separados?',
  answer: 'Puede ayudarte a evaluar el contexto, recuperar serenidad y decidir si un acercamiento es apropiado. Cuanto más tiempo haya pasado, más importante es respetar los límites actuales y no asumir que el vínculo puede retomarse automáticamente.'
}, {
  question: '¿Y si él ya está con otra persona?',
  answer: 'El material no propone interferir en relaciones ajenas. Puede servirte para entender tus emociones, cuidar tus límites y tomar decisiones que respeten a todas las personas involucradas.'
}, {
  question: '¿Cómo recibo el material?',
  answer: 'El acceso es digital e inmediato una vez realizada la compra. Puedes leerlo desde tu móvil, tablet u ordenador y volver a consultarlo cuando lo necesites.'
}, {
  question: '¿Esto es manipulación o es algo ético?',
  answer: 'No es una promesa de controlar a otra persona. La guía se centra en comunicación responsable, autoconocimiento y límites claros. Una reconexión sana solo puede existir cuando hay respeto y voluntad de ambas partes.'
}, {
  question: '¿Qué pasa si no funciona en mi caso?',
  answer: 'Tienes 7 días para revisar el material. Si consideras que no es adecuado para ti, puedes solicitar la devolución de tu inversión según las condiciones de la garantía.'
}];
export function ReconquistaLandingPage() {
  const shouldReduceMotion = useReducedMotion();
  const [showFloatingCta, setShowFloatingCta] = useState(false);
  useEffect(() => {
    const offerSection = document.getElementById('oferta');
    const updateFloatingCta = () => {
      const isMobile = window.innerWidth < 640;
      const showAfter = isMobile ? 64 : window.innerHeight * 0.35;
      const hasStartedScrolling = window.scrollY > showAfter;
      const offerIsVisible = offerSection ? offerSection.getBoundingClientRect().top < window.innerHeight * 0.72 && offerSection.getBoundingClientRect().bottom > 0 : false;
      setShowFloatingCta(hasStartedScrolling && !offerIsVisible);
    };
    window.addEventListener('scroll', updateFloatingCta, {
      passive: true
    });
    updateFloatingCta();
    return () => window.removeEventListener('scroll', updateFloatingCta);
  }, []);
  const reveal = (delay = 0) => ({
    initial: shouldReduceMotion ? false : {
      opacity: 0,
      y: 18
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.14
    },
    transition: {
      duration: 0.55,
      delay,
      ease: [0.22, 1, 0.36, 1]
    }
  });
  const scrollToOffer = () => document.getElementById('oferta')?.scrollIntoView({
    behavior: 'smooth',
    block: 'center'
  });
  return <div className="min-h-screen w-full overflow-hidden bg-[#100913] text-[#f5eee6]" data-id="element-11">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#100913]/95 backdrop-blur-sm" data-id="element-12">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-center px-5 sm:px-8" data-id="element-13">
          <a href="#inicio" className="text-sm font-semibold tracking-[0.2em] text-[#f5eee6] outline-none focus-visible:ring-2 focus-visible:ring-[#e849d8]" data-id="element-14">
            EL CÓDIGO DE LA RECONQUISTA
          </a>
        </div>
      </header>

      <main id="inicio" data-id="element-15">
        <section className="border-b border-white/10" data-id="element-16">
          <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-14 sm:px-8 md:py-20 lg:grid-cols-[1.02fr_.98fr] lg:gap-16 lg:py-24" data-id="element-17">
            <motion.div {...reveal()} className="order-2 lg:order-1" data-id="element-18">
              <p className="section-kicker" data-id="element-19">
                Método psicológico de reconexión emocional
              </p>
              <h1 className="mt-5 font-serif text-4xl leading-[1.05] text-[#fff9f1] sm:text-5xl lg:text-6xl" data-id="element-20">
                El Código de la{' '}
                <em className="font-medium text-[#f1a8e8]" data-id="element-21">Reconquista</em>
              </h1>
              <p className="mt-5 max-w-xl font-serif text-2xl leading-tight text-[#f6eaf4] sm:text-3xl" data-id="element-22">
                Descubre palabras y pasos para reabrir la conversación, incluso
                cuando ahora parece existir indiferencia.
              </p>
              <p className="mt-5 max-w-xl text-base leading-7 text-[#c8bdc9] sm:text-lg sm:leading-8" data-id="element-23">
                No es magia ni una fórmula para controlar a alguien. Es una guía
                de psicología emocional aplicada para comunicarte con más calma,
                respeto y coherencia mientras recuperas tu propio centro.
              </p>
              <div className="mt-8 flex flex-wrap items-end gap-x-4 gap-y-1" data-id="element-24">
                <p className="font-serif text-4xl text-[#fff8f0]" data-id="element-25">USD$13,90</p>
              </div>
              <p className="mt-1 text-xs text-[#c8bdc9]" data-id="element-26">
                El producto se cobra en moneda local.
              </p>
              <p className="mt-2 text-xs text-[#e8b8e2]" data-id="element-27">
                Pago único · Acceso inmediato · Oferta disponible por tiempo
                limitado
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-28">
                <button onClick={scrollToOffer} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#e849d8] px-6 py-3.5 text-sm font-bold text-[#170b19] outline-none transition hover:bg-[#f16be4] focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-[#100913]" data-id="element-29">
                  Quiero el acceso ahora{' '}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-30" />
                </button>
                <p className="flex items-center gap-1.5 text-xs text-[#a89cae]" data-id="element-31">
                  <LockKeyholeIcon className="h-3.5 w-3.5" data-id="element-32" /> Visa · Mastercard
                  · PayPal · Pago seguro
                </p>
              </div>
            </motion.div>

            <motion.div {...reveal(0.12)} className="relative order-1 min-h-[430px] sm:min-h-[510px] lg:order-2" data-id="element-33">
              <img src={images.hero} alt="Mujer mirando su teléfono mientras reflexiona" className="absolute inset-0 h-full w-full rounded-2xl object-cover opacity-80" data-id="element-34" />
              <div className="absolute inset-0 rounded-2xl bg-[#160918]/35" data-id="element-35" />
              <div className="absolute inset-x-5 bottom-0 flex items-end justify-center sm:inset-x-10" data-id="element-36"></div>
              <div className="absolute left-5 top-5 rounded-full border border-white/20 bg-[#170d1a]/90 px-3 py-1.5 text-xs font-semibold text-[#f5eee6]" data-id="element-37">
                Ebook digital · Acceso inmediato
              </div>
            </motion.div>
          </div>
        </section>

        <section className="bg-[#150b1b] py-16 sm:py-24" data-id="element-38">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 lg:grid-cols-[.85fr_1.15fr] lg:items-center" data-id="element-39">
            <motion.figure {...reveal()} className="overflow-hidden rounded-2xl border border-white/10" data-id="element-40">
              <img src={images.problem} alt="Teléfono con mensajes en una habitación oscura" className="h-72 w-full object-cover sm:h-96" data-id="element-41" />
            </motion.figure>
            <motion.div {...reveal(0.08)} data-id="element-42">
              <p className="section-kicker" data-id="element-43">La verdad que nadie te dice</p>
              <h2 className="section-title mt-4" data-id="element-44">
                La reconquista no es cuestión de suerte: necesita intención,
                límites y pasos claros.
              </h2>
              <div className="mt-7 space-y-4 text-base leading-7 text-[#c8bdc9]" data-id="element-45">
                <p data-id="element-46">
                  La diferencia no está en la belleza, la edad o el tiempo que
                  estuvieron juntos. Está en comprender qué ocurre
                  emocionalmente después de una ruptura y en saber actuar sin
                  reaccionar desde el miedo.
                </p>
                <p data-id="element-47">
                  Muchas personas hacen lo opuesto a lo que les ayuda: se
                  apresuran, presionan o se dejan guiar por señales aisladas.
                  Sin un mapa, cada decisión se vuelve más pesada.
                </p>
              </div>
              <p className="mt-7 border-l-2 border-[#e849d8] pl-4 font-serif text-xl leading-8 text-[#f6eaf4]" data-id="element-48">
                No puedes decidir por otra persona, pero sí puedes elegir una
                forma más serena, honesta y respetuosa de acercarte.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="border-y border-white/10 bg-[#100913] py-16 sm:py-24" data-id="element-49">
          <div className="mx-auto max-w-6xl px-5 sm:px-8" data-id="element-50">
            <motion.div {...reveal()} className="max-w-2xl" data-id="element-51">
              <p className="section-kicker" data-id="element-52">Por qué no está funcionando</p>
              <h2 className="section-title mt-4" data-id="element-53">
                Probablemente ya intentaste esto… y las cosas se hicieron más
                difíciles.
              </h2>
            </motion.div>
            <div className="mt-10 grid gap-x-10 gap-y-4 md:grid-cols-2" data-id="element-54">
              {failurePoints.map((point, index) => <motion.div {...reveal(index * 0.04)} key={point} className="flex gap-3 border-b border-white/10 py-4 text-sm leading-7 text-[#c8bdc9]" data-id="element-55">
                  <span className="mt-1.5 text-base text-[#e849d8]" aria-hidden="true" data-id="element-56">
                    ×
                  </span>
                  <p data-id="element-57">{point}</p>
                </motion.div>)}
            </div>
            <motion.p {...reveal(0.1)} className="mt-8 max-w-3xl rounded-r-xl border-l-2 border-[#e849d8] bg-[#1a0d21] px-5 py-5 text-sm leading-7 text-[#e5d9e5]" data-id="element-58">
              Nada de esto define tu valor. Simplemente nadie te enseñó a
              orientar una conversación difícil después de una ruptura. Dejar de
              adivinar es el primer paso para actuar con un plan que también te
              cuide.
            </motion.p>
          </div>
        </section>

        <section className="bg-[#1d0c26] py-16 sm:py-24" data-id="element-59">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_.85fr] lg:items-center" data-id="element-60">
            <motion.div {...reveal()} data-id="element-61">
              <p className="section-kicker" data-id="element-62">Olvida las fórmulas vacías</p>
              <h2 className="section-title mt-4" data-id="element-63">
                Reconectar no es provocar celos ni perseguir. Es crear
                condiciones para una conversación más real.
              </h2>
              <div className="mt-7 space-y-4 leading-7 text-[#c8bdc9]" data-id="element-64">
                <p data-id="element-65">
                  Muchos consejos se basan en intuición popular: “date tu
                  lugar”, “haz que te extrañe” o “mejora por ti”. Aunque algunas
                  ideas pueden ser útiles, no reemplazan la claridad emocional
                  ni el respeto por los límites.
                </p>
                <p data-id="element-66">
                  El Código de la Reconquista no te enseña a forzar una
                  respuesta. Te enseña a posicionarte con mayor seguridad, a
                  reconocer el contexto y a expresar lo que sientes sin
                  abandonar tu dignidad.
                </p>
              </div>
            </motion.div>
            <motion.figure {...reveal(0.1)} className="relative overflow-hidden rounded-2xl" data-id="element-67">
              <img src={images.transformation} alt="Mujer serena frente a una ventana" className="h-80 w-full object-cover sm:h-[430px]" data-id="element-68" />
              <figcaption className="absolute inset-x-0 bottom-0 bg-[#100913]/85 p-5 text-sm leading-6 text-[#eee3ec]" data-id="element-69">
                Una reconexión sana empieza por recuperar tu calma y tu voz.
              </figcaption>
            </motion.figure>
          </div>
        </section>

        <section className="bg-[#100913] py-16 sm:py-24" data-id="element-70">
          <div className="mx-auto max-w-6xl px-5 sm:px-8" data-id="element-71">
            <motion.div {...reveal()} className="mx-auto max-w-2xl text-center" data-id="element-72">
              <p className="section-kicker" data-id="element-73">Experiencias compartidas</p>
              <h2 className="section-title mt-4" data-id="element-74">
                Cuando cambias el enfoque, también cambia la forma de habitar el
                proceso.
              </h2>
            </motion.div>
            <div className="mt-10 grid gap-4 md:grid-cols-2" data-id="element-75">
              <motion.blockquote {...reveal()} className="rounded-2xl border border-white/10 bg-[#1a0d21] p-6" data-id="element-76">
                <p className="font-serif text-xl leading-8 text-[#f6eaf4]" data-id="element-77">
                  “Tenía cuatro meses sin hablar con él. Después de aplicar el
                  método sin exagerar ni esperar milagros, él escribió primero
                  doce días después. Hoy nuestra relación es completamente
                  diferente.”
                </p>
                <footer className="mt-5 text-sm text-[#e849d8]" data-id="element-78">
                  — Valentina M., Colombia
                </footer>
              </motion.blockquote>
              <motion.blockquote {...reveal(0.08)} className="rounded-2xl border border-white/10 bg-[#1a0d21] p-6" data-id="element-79">
                <p className="font-serif text-xl leading-8 text-[#f6eaf4]" data-id="element-80">
                  “Lo que más me impactó fue entender por qué todo lo que hacía
                  antes lo alejaba más. Al cambiar el enfoque, la dinámica entre
                  nosotros cambió en pocos días.”
                </p>
                <footer className="mt-5 text-sm text-[#e849d8]" data-id="element-81">
                  — Sofía R., México
                </footer>
              </motion.blockquote>
            </div>
            <p className="mx-auto mt-6 max-w-2xl text-center text-xs leading-6 text-[#9f92a1]" data-id="element-82">
              Los testimonios reflejan experiencias personales. Los resultados y
              los tiempos pueden variar según cada relación y sus
              circunstancias.
            </p>
          </div>
        </section>

        <section className="border-y border-white/10 bg-[#150b1b] py-14 sm:py-20" data-id="element-83">
          <div className="mx-auto grid max-w-6xl gap-4 px-5 text-center sm:grid-cols-2 sm:px-8 lg:grid-cols-4" data-id="element-84">
            {[['91%', 'de lectoras reportó un cambio notable en menos de 3 semanas'], ['400+', 'testimonios compartidos en América Latina'], ['⚡', 'Acceso inmediato al comprar'], ['◌', 'Disponible desde cualquier dispositivo']].map(([number, label], index) => <motion.div {...reveal(index * 0.05)} key={label} className="border-white/10 px-4 py-4 lg:border-r last:border-0" data-id="element-85">
                <p className="font-serif text-4xl text-[#f1a8e8]" data-id="element-86">{number}</p>
                <p className="mt-2 text-sm leading-6 text-[#c8bdc9]" data-id="element-87">{label}</p>
              </motion.div>)}
          </div>
          <p className="mx-auto mt-6 max-w-2xl px-5 text-center text-xs leading-6 text-[#9f92a1]" data-id="element-88">
            Datos basados en testimonios de clientas y encuestas internas; no
            constituyen una garantía de resultados.
          </p>
        </section>

        <section className="bg-[#100913] py-16 sm:py-24" data-id="element-89">
          <div className="mx-auto max-w-6xl px-5 sm:px-8" data-id="element-90">
            <motion.div {...reveal()} className="max-w-2xl" data-id="element-91">
              <p className="section-kicker" data-id="element-92">Qué obtienes</p>
              <h2 className="section-title mt-4" data-id="element-93">
                Un sistema completo, no consejos sueltos.
              </h2>
              <p className="mt-5 leading-7 text-[#bfb2c1]" data-id="element-94">
                No es un artículo de internet ni un video genérico. Es una ruta
                secuencial con herramientas que puedes aplicar desde hoy.
              </p>
            </motion.div>
            <div className="mt-10 grid gap-4 md:grid-cols-3" data-id="element-95">
              {approachCards.map(({
              icon: CardIcon,
              title,
              description
            }, index) => <motion.article {...reveal(index * 0.06)} key={title} className="rounded-2xl border border-white/10 bg-[#1a0d21] p-6" data-id="element-96">
                    <CardIcon className="h-6 w-6 text-[#e849d8]" aria-hidden="true" data-id="element-97" />
                    <h3 className="mt-7 font-serif text-2xl text-[#fff8f0]" data-id="element-98">
                      {title}
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-[#bfb2c1]" data-id="element-99">
                      {description}
                    </p>
                  </motion.article>)}
            </div>
          </div>
        </section>

        <section className="bg-[#1d0c26] py-16 sm:py-24" data-id="element-100">
          <div className="mx-auto max-w-6xl px-5 sm:px-8" data-id="element-101">
            <motion.div {...reveal()} className="mx-auto max-w-2xl text-center" data-id="element-102">
              <p className="section-kicker" data-id="element-103">Todo lo que incluye</p>
              <h2 className="section-title mt-4" data-id="element-104">
                Lo que recibes en tu acceso.
              </h2>
            </motion.div>
            <div className="mt-12 divide-y divide-white/10 border-y border-white/10" data-id="element-105">
              {inclusions.map((inclusion, index) => <motion.article {...reveal(index * 0.04)} key={inclusion.title} className="grid gap-3 py-6 sm:grid-cols-[110px_1fr_1fr] sm:gap-6" data-id="element-106">
                  <div data-id="element-107">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#e849d8]" data-id="element-108">
                      {inclusion.label}
                    </p>
                    {inclusion.bonusValue && <div className="mt-2 flex items-center gap-2" data-id="element-109">
                        <span className="text-xs font-medium text-[#a89cae] line-through" data-id="element-110">
                          {inclusion.bonusValue}
                        </span>
                        <span className="rounded-full bg-[#2d774a] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#dcffe8]" data-id="element-111">
                          Gratis
                        </span>
                      </div>}
                  </div>
                  <h3 className="font-serif text-2xl text-[#fff8f0]" data-id="element-112">
                    {inclusion.title}
                  </h3>
                  <p className="text-sm leading-7 text-[#bfb2c1]" data-id="element-113">
                    {inclusion.description}
                  </p>
                </motion.article>)}
            </div>
          </div>
        </section>

        <section className="bg-[#100913] py-16 sm:py-24" data-id="element-114">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_.82fr] lg:items-center" data-id="element-115">
            <motion.div {...reveal()} data-id="element-116">
              <p className="section-kicker" data-id="element-117">¿Para quién es esto?</p>
              <h2 className="section-title mt-4" data-id="element-118">Esto es para ti si…</h2>
              <ul className="mt-8 grid gap-4 sm:grid-cols-2" data-id="element-119">
                {qualificationItems.map(item => <li key={item} className="flex gap-3 text-sm leading-7 text-[#d6cad5]" data-id="element-120">
                    <span className="mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#4c174d]" data-id="element-121">
                      <CheckIcon className="h-3.5 w-3.5 text-[#f182e7]" data-id="element-122" />
                    </span>
                    {item}
                  </li>)}
              </ul>
            </motion.div>
            <motion.aside {...reveal(0.1)} className="rounded-2xl border border-[#e849d8]/30 bg-[#1a0d21] p-6" data-id="element-123">
              <ShieldCheckIcon className="h-7 w-7 text-[#e849d8]" aria-hidden="true" data-id="element-124" />
              <h3 className="mt-5 font-serif text-2xl text-[#fff8f0]" data-id="element-125">
                Un límite importante
              </h3>
              <p className="mt-3 text-sm leading-7 text-[#c8bdc9]" data-id="element-126">
                Este material no es para manipular a alguien que nunca te quiso,
                interferir en una relación ajena o volver con una persona tóxica
                o abusiva. Está pensado para vínculos donde hubo amor real,
                respeto y una conexión que ambas personas puedan elegir
                reconstruir.
              </p>
            </motion.aside>
          </div>
        </section>

        <section id="oferta" className="scroll-mt-24 bg-[#3a1041] py-16 sm:py-24" data-id="element-127">
          <div className="mx-auto grid max-w-5xl gap-10 px-5 sm:px-8 lg:grid-cols-[.85fr_1.15fr] lg:items-center" data-id="element-128">
            <motion.div {...reveal()} className="flex justify-center" data-id="element-129"></motion.div>
            <motion.div {...reveal(0.08)} className="rounded-2xl border border-[#f09de7]/30 bg-[#190c20] p-6 sm:p-9" data-id="element-130">
              <p className="section-kicker" data-id="element-131">Resumen del acceso</p>
              <h2 className="mt-4 font-serif text-3xl leading-tight text-[#fff8f0] sm:text-4xl" data-id="element-132">
                Todo lo que tienes al comprar hoy.
              </h2>
              <ul className="mt-7 space-y-4" data-id="element-133">
                {accessItems.map(item => <li key={item.title} className="flex gap-3 text-sm leading-6 text-[#e4d9e4]" data-id="element-134">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#ef76e2]" data-id="element-135" />
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-136">
                      <span data-id="element-137">{item.title}</span>
                      {item.bonusValue && <>
                          <span className="text-xs text-[#a89cae] line-through" data-id="element-138">
                            {item.bonusValue}
                          </span>
                          <span className="rounded-full bg-[#2d774a] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#dcffe8]" data-id="element-139">
                            Gratis
                          </span>
                        </>}
                    </span>
                  </li>)}
              </ul>
              <div className="my-8 border-y border-white/10 py-5" data-id="element-140">
                <p className="text-xs uppercase tracking-[0.15em] text-[#bcaebd]" data-id="element-141">
                  Inversión
                </p>
                <p className="mt-1 font-serif text-5xl text-[#fff8f0]" data-id="element-142">
                  USD$13,90
                </p>
                <p className="mt-2 text-xs text-[#c8bdc9]" data-id="element-143">
                  El producto se cobra en moneda local.
                </p>
                <p className="mt-2 text-xs text-[#bcaebd]" data-id="element-144">
                  Pago único · Acceso inmediato y permanente
                </p>
              </div>
              <a href="https://el-codigo-de-la-reconquista.impultienda.ar/checkout" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#e849d8] px-6 py-4 text-sm font-bold text-[#190b1c] outline-none transition hover:bg-[#f16be4] focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-[#190c20]" data-id="element-145">
                Quiero acceso inmediato <ArrowRightIcon className="h-4 w-4" data-id="element-146" />
              </a>
              <p className="mt-4 flex items-center justify-center gap-2 text-center text-xs text-[#bcaebd]" data-id="element-147">
                <LockKeyholeIcon className="h-3.5 w-3.5" data-id="element-148" />
                Pago seguro · Acceso al instante · Garantía de 7 días
              </p>
            </motion.div>
          </div>
        </section>

        <section className="bg-[#150b1b] py-16 sm:py-24" data-id="element-149">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 lg:grid-cols-[.9fr_1.1fr] lg:items-center" data-id="element-150">
            <motion.figure {...reveal()} className="overflow-hidden rounded-2xl" data-id="element-151">
              <img src={images.author} alt="Autora escribiendo en su escritorio" className="h-80 w-full object-cover sm:h-[430px]" data-id="element-152" />
            </motion.figure>
            <motion.div {...reveal(0.08)} data-id="element-153">
              <p className="section-kicker" data-id="element-154">Sobre la autora</p>
              <h2 className="section-title mt-4" data-id="element-155">
                Especialista en psicología de las relaciones.
              </h2>
              <div className="mt-6 space-y-4 leading-7 text-[#c8bdc9]" data-id="element-156">
                <p data-id="element-157">
                  Con más de ocho años estudiando la psicología del deseo y las
                  dinámicas de pareja en América Latina, desarrollé este método
                  después de acompañar a cientos de mujeres a comprender por qué
                  algunas formas de comunicación ayudan y otras pueden
                  profundizar la distancia.
                </p>
                <p data-id="element-158">
                  El Código de la Reconquista reúne esa investigación en un
                  material práctico para quienes buscan herramientas reales, no
                  teorías vacías. Siempre con una premisa clara: una relación
                  sana requiere respeto, consentimiento y voluntad mutua.
                </p>
                <p className="font-serif text-xl text-[#f5d8f0]" data-id="element-159">
                  — Nina Salas, autora
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="bg-[#100913] py-16 sm:py-24" data-id="element-160">
          <div className="mx-auto grid max-w-5xl gap-8 px-5 sm:px-8 lg:grid-cols-[.72fr_1.28fr] lg:items-center" data-id="element-161">
            <motion.div {...reveal()} className="rounded-2xl border border-[#e849d8]/25 bg-[#1a0d21] p-7 text-center" data-id="element-162">
              <p className="font-serif text-5xl text-[#f1a8e8]" data-id="element-163">7 días</p>
              <p className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-[#e849d8]" data-id="element-164">
                Sin riesgo
              </p>
              <h2 className="mt-5 font-serif text-3xl text-[#fff8f0]" data-id="element-165">
                Garantía total de 7 días
              </h2>
            </motion.div>
            <motion.div {...reveal(0.08)} data-id="element-166">
              <p className="section-kicker" data-id="element-167">Tu garantía</p>
              <h2 className="section-title mt-4" data-id="element-168">
                Tómate una semana para revisar el método con calma.
              </h2>
              <p className="mt-5 leading-7 text-[#c8bdc9]" data-id="element-169">
                Si después de revisar el material consideras que no es para ti,
                puedes solicitar el 100% de tu inversión. Sin formularios
                interminables ni burocracia. El objetivo es que tomes una
                decisión informada y tranquila.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="bg-[#150b1b] py-16 sm:py-24" data-id="element-170">
          <div className="mx-auto max-w-3xl px-5 sm:px-8" data-id="element-171">
            <motion.div {...reveal()} data-id="element-172">
              <p className="section-kicker text-center" data-id="element-173">Preguntas frecuentes</p>
              <h2 className="section-title mt-4 text-center" data-id="element-174">
                Todo lo que necesitas saber.
              </h2>
            </motion.div>
            <div className="mt-9" data-id="element-175">
              <FaqAccordion items={faqs} data-id="element-176" />
            </div>
          </div>
        </section>
      </main>

      {showFloatingCta && <motion.button type="button" onClick={scrollToOffer} initial={shouldReduceMotion ? false : {
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.22,
      ease: [0.22, 1, 0.36, 1]
    }} className="fixed inset-x-4 bottom-4 z-50 flex items-center justify-center gap-2 rounded-full bg-[#e849d8] px-6 py-4 text-sm font-bold text-[#190b1c] shadow-[0_14px_36px_rgba(0,0,0,0.45)] outline-none transition hover:bg-[#f16be4] focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-[#100913] sm:inset-x-auto sm:right-6 sm:bottom-6" data-id="element-177">
          Ver oferta y bonus <ArrowRightIcon className="h-4 w-4" data-id="element-178" />
        </motion.button>}

      <footer className="bg-[#0b0710] px-5 py-8 text-center text-xs text-[#8f8491] sm:px-8" data-id="element-179">
        <p className="font-semibold tracking-[0.14em] text-[#d6cbd7]" data-id="element-180">
          EL CÓDIGO DE LA RECONQUISTA
        </p>
        <p className="mt-3" data-id="element-181">
          Una guía para comunicarte con respeto, claridad y amor propio.
        </p>
        <p className="mt-5" data-id="element-182">© 2026 · Todos los derechos reservados</p>
      </footer>
    </div>;
}